import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface PageHeaderProps {
  /** Tiny uppercase label above the title (e.g. "Sales · This week") */
  eyebrow?: string
  /** Page title — controlled with weight + color, not massive scale */
  title: string
  /** Optional secondary line under the title */
  subtitle?: string
  /** Optional left-side glyph (color chip, icon, avatar, etc.) */
  icon?: ReactNode
  /** Right-side cluster — buttons, badges, filters */
  actions?: ReactNode
  /** Tight (compact) header for dense pages, defaults to comfortable */
  size?: 'comfortable' | 'tight'
  /** Hide gradient orb backdrop (useful for dense pages with many filters) */
  flat?: boolean
  /** Live indicator — small pulsing dot beside the eyebrow when true */
  live?: boolean
  className?: string
}

/**
 * Universal dashboard page header.
 *
 * Used to give every top-level page a coherent, premium identity:
 * - Soft brand-gradient orbs in the background (`.hero-card`)
 * - Gradient-clipped title that reads as "this is the page"
 * - Optional eyebrow for context (date range, plan tier)
 * - Right-aligned action cluster (filters, primary CTA)
 *
 * Mobile: stacks actions under the title; orbs scale down via overflow.
 */
export function PageHeader({
  eyebrow,
  title,
  subtitle,
  icon,
  actions,
  size = 'comfortable',
  flat = false,
  live = false,
  className,
}: PageHeaderProps) {
  return (
    <div
      className={cn(
        'rounded-2xl border bg-card shadow-tinted mb-5 sm:mb-6',
        !flat && 'hero-card',
        className,
      )}
    >
      {!flat && <div className="hero-card-orbs" aria-hidden="true" />}
      <div
        className={cn(
          'flex flex-col gap-4 sm:flex-row sm:items-center',
          size === 'comfortable' ? 'p-5 sm:p-7' : 'p-4 sm:p-5',
        )}
      >
        {icon && <div className="shrink-0">{icon}</div>}
        <div className="flex-1 min-w-0">
          {(eyebrow || live) && (
            <div className="flex items-center gap-2 mb-1.5">
              {live && (
                <span className="relative inline-flex h-2 w-2 shrink-0">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-50 animate-breath" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
              )}
              {eyebrow && (
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  {eyebrow}
                </p>
              )}
            </div>
          )}
          {/* Hierarchy via weight + tracking, not just scale (taste rule) */}
          <h1
            className={cn(
              'font-heading font-semibold tracking-tighter leading-none text-foreground',
              size === 'comfortable' ? 'text-3xl sm:text-4xl' : 'text-2xl sm:text-3xl',
            )}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-2.5 max-w-[65ch] leading-relaxed">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  )
}
